/**
 * Entity branding as CSS custom properties on the widget's host element.
 *
 * One entity config gives every widget the same colour, font, radius and
 * typography tokens.
 */

import type { ResolvedWidgetConfig } from "../types";

export function applyBranding(
  host: HTMLElement,
  config: ResolvedWidgetConfig,
): void {
  const { branding, panelWidth, zIndex } = config;

  // Color tokens
  host.style.setProperty("--hana-primary", branding.primaryColor);
  host.style.setProperty("--hana-primary-hover", branding.primaryHover);
  host.style.setProperty("--hana-secondary", branding.secondaryColor);
  host.style.setProperty("--hana-bg", branding.backgroundColor);
  host.style.setProperty("--hana-surface", branding.surfaceColor);
  host.style.setProperty("--hana-text", branding.textColor);
  host.style.setProperty("--hana-text-muted", branding.textSecondary);
  host.style.setProperty("--hana-border", branding.borderColor);
  host.style.setProperty("--hana-bubble-bot-bg", branding.botBubbleColor);
  host.style.setProperty(
    "--hana-suggested-bg",
    branding.suggestedPillBackground,
  );
  host.style.setProperty(
    "--hana-suggested-border",
    branding.suggestedPillBorder,
  );
  host.style.setProperty("--hana-button-primary-bg", branding.primaryColor);
  host.style.setProperty(
    "--hana-button-primary-hover-bg",
    branding.primaryHover,
  );
  host.style.setProperty(
    "--hana-button-outline-color",
    branding.primaryColor,
  );
  host.style.setProperty("--hana-cta-radius", `${branding.ctaRadius}px`);

  // Typography
  host.style.setProperty("--hana-font", branding.fontFamily);
  host.style.setProperty(
    "--hana-button-radius",
    `${branding.buttonRadius}px`,
  );
  host.style.setProperty(
    "--hana-heading-size",
    branding.typography.heading.size,
  );
  host.style.setProperty(
    "--hana-heading-weight",
    branding.typography.heading.weight,
  );
  host.style.setProperty("--hana-body-size", branding.typography.body.size);
  host.style.setProperty(
    "--hana-body-weight",
    branding.typography.body.weight,
  );
  host.style.setProperty(
    "--hana-caption-size",
    branding.typography.caption.size,
  );
  host.style.setProperty(
    "--hana-caption-weight",
    branding.typography.caption.weight,
  );

  // Extended typography tiers (FRD Section 8.3)
  const typo = branding.typography;
  if (typo.headingLarge) {
    host.style.setProperty("--hana-heading-lg-size", typo.headingLarge.size);
    host.style.setProperty(
      "--hana-heading-lg-weight",
      typo.headingLarge.weight,
    );
  }
  if (typo.bodyMedium) {
    host.style.setProperty("--hana-body-md-size", typo.bodyMedium.size);
    host.style.setProperty("--hana-body-md-weight", typo.bodyMedium.weight);
  }
  if (typo.bodySmall) {
    host.style.setProperty("--hana-body-sm-size", typo.bodySmall.size);
    host.style.setProperty("--hana-body-sm-weight", typo.bodySmall.weight);
  }
  if (typo.labelSmall) {
    host.style.setProperty("--hana-label-sm-size", typo.labelSmall.size);
    host.style.setProperty("--hana-label-sm-weight", typo.labelSmall.weight);
  }

  // Layout
  host.style.setProperty("--hana-panel-width", `${panelWidth}px`);
  host.style.setProperty("--hana-z-index", String(zIndex));
}
